import { ReactElement, useCallback, useMemo, useState } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { CabButton, CabModal, CabTextInput } from '@CabComponents';
import colors from '../../../colors';
import { scheduling } from '../../../store/cabinetApi/scheduling';
import { MeetingTemplate } from '../../../store/cabinetApi/generated/scheduling';

const sortTemplates = (list: MeetingTemplate[]) => (
  [...list].sort(
    (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  )
);

export const SavedTemplatesTab = (): ReactElement => {
  const {
    data: templatesResponse,
    isLoading: templatesLoading,
    refetch: refetchTemplates,
  } = scheduling.useAppSchedulingMeetingTemplatesListQuery();
  const [createTemplate, { isLoading: createLoading }] = scheduling.useAppSchedulingMeetingTemplatesCreateMutation();
  const [updateTemplate, { isLoading: updateLoading }] = scheduling.useAppSchedulingMeetingTemplatesUpdateMutation();
  const [deleteTemplate, { isLoading: deleteLoading }] = scheduling.useAppSchedulingMeetingTemplatesDestroyMutation();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<MeetingTemplate | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftTitle, setDraftTitle] = useState('');
  const [draftDescription, setDraftDescription] = useState('');

  const openNew = useCallback(() => {
    setEditing(null);
    setDraftName('');
    setDraftTitle('');
    setDraftDescription('');
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((row: MeetingTemplate) => {
    setEditing(row);
    setDraftName(row.name);
    setDraftTitle(row.title || '');
    setDraftDescription(row.description || '');
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditing(null);
  }, []);

  const handleSave = useCallback(async () => {
    const name = draftName.trim();
    const title = draftTitle.trim();
    const description = draftDescription.trim();
    if (!name) return;

    const payload = {
      name,
      title: title || '',
      description: description || undefined,
    };
    if (editing) {
      await updateTemplate({
        id: editing.id,
        meetingTemplateRequest: payload,
      }).unwrap();
    } else {
      await createTemplate(payload).unwrap();
    }
    await refetchTemplates();
    closeModal();
  }, [closeModal, createTemplate, draftDescription, draftName, draftTitle, editing, refetchTemplates, updateTemplate]);

  const handleDelete = useCallback(async () => {
    if (!editing) return;
    await deleteTemplate(editing.id).unwrap();
    await refetchTemplates();
    closeModal();
  }, [closeModal, deleteTemplate, editing, refetchTemplates]);

  const templates = useMemo(
    () => sortTemplates(templatesResponse || []),
    [templatesResponse]
  );
  const isMutating = createLoading || updateLoading || deleteLoading;

  const emptyHint = useMemo(() => templates.length === 0, [templates.length]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2
        }}>
        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          Create reusable meeting templates for new meetings.
        </Typography>
        <Box>
          <CabButton onClick={openNew} disabled={isMutating}>
            New template
          </CabButton>
        </Box>

        <TableContainer component={Paper} variant="outlined" sx={{ borderColor: colors.black200 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Template Name</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {emptyHint ? (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        py: 2
                      }}>
                      {templatesLoading
                        ? 'Loading templates...'
                        : 'No saved templates yet. Create one to reuse in the scheduling meeting panel.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                templates.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={() => !isMutating && openEdit(row)}
                    sx={{ cursor: 'pointer', '&:last-child td': { border: 0 } }}
                  >
                    <TableCell sx={{ maxWidth: 220, fontWeight: 600 }}>{row.name}</TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>{row.title || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 480 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                        {row.description || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <CabModal
        open={modalOpen}
        onClose={closeModal}
        closeIcon
        title={editing ? 'Edit template' : 'New template'}
        noFullScreen
        actionButtons={(
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              flexWrap: "wrap",
              gap: 1
            }}>
            <Box
              sx={{
                display: "flex",
                gap: 1
              }}>
              {editing && (
                <CabButton buttonType="secondary" color="error" onClick={handleDelete}>
                  Delete
                </CabButton>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 1
              }}>
              <CabButton buttonType="secondary" onClick={closeModal} disabled={isMutating}>
                Cancel
              </CabButton>
              <CabButton onClick={handleSave} disabled={!draftName.trim() || isMutating}>
                Save
              </CabButton>
            </Box>
          </Box>
        )}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            paddingTop: 1
          }}>
          <CabTextInput
            label="Template Name"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            required
          />
          <CabTextInput
            label="Event Title"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            multiline
            minRows={1}
            maxRows={3}
          />
          <CabTextInput
            label="Event Description"
            value={draftDescription}
            onChange={(e) => setDraftDescription(e.target.value)}
            multiline
            minRows={3}
            maxRows={6}
          />
        </Box>
      </CabModal>
    </>
  );
};
